import {
  Bot,
  Briefcase,
  CalendarDays,
  ClipboardList,
  Download,
  FileSpreadsheet,
  Gauge,
  LayoutDashboard,
  Megaphone,
  MessagesSquare,
  Newspaper,
  Palette,
  UserCheck,
  Users,
  UsersRound,
} from 'lucide-react'

import { queryKeys } from '@/api/apis'

import { paths } from './paths'
import { ROLES, ROUTE_PATHS } from './routeAccess'

/**
 * THE route configuration. Every route in the application is declared here and
 * mapped into the router by `app/router.jsx`; nothing else defines a route.
 *
 * Per-route fields:
 *   layout       'dashboard' | 'auth'  which shell wraps this branch
 *   name         label for navigation, breadcrumbs and the document title
 *   path         absolute path, always taken from `paths.js`
 *   element      component factory for a lazily loaded page
 *   index        index route of its parent
 *   icon         sidebar icon (lucide component)
 *   showInNav    render a sidebar entry for this route
 *   section      'main' | 'footer' sidebar grouping
 *   access       route path the user must hold in `access_control` to reach it
 *                (enforced — see routeAccess.js). Omit for a route every
 *                signed-in user may open.
 *   children     nested routes; rendered inside the parent's <Outlet />
 */

/** Wraps a dynamic import so the router can code-split each page. */
const page = (loader) => async () => ({ Component: (await loader()).default })

export const routes = [
  {
    id: 'auth',
    layout: 'auth',
    public: true,
    children: [
      {
        id: 'login',
        name: 'Sign in',
        path: paths.login,
        hideCrumb: true,
        element: page(() => import('@/features/auth/pages/LoginPage')),
      },
    ],
  },
  {
    id: 'app',
    layout: 'dashboard',
    children: [
      {
        // Not a fixed `redirectTo`: where `/` lands depends on what the user's
        // role can reach. See routes/landing.js.
        id: 'root-redirect',
        index: true,
        element: page(() => import('./LandingRedirect')),
      },
      {
        id: 'dashboard',
        name: 'Overview',
        path: paths.dashboard,
        icon: LayoutDashboard,
        showInNav: true,
        section: 'main',
        // Not everyone lands here: `/` redirects to the first area this user is
        // actually given (see routes/landing.js).
        access: ROUTE_PATHS.dashboard,
        element: page(() => import('@/features/dashboard/pages/DashboardPage')),

        /**
         * Nested children are supported at any depth. A parent with children
         * renders its own page only through an `index` child; the rest appear
         * inside its <Outlet />. Example:
         *
         * children: [
         *   { id: 'dashboard-index', index: true, element: page(() => import('...')) },
         *   { id: 'dashboard-reports', name: 'Reports', path: paths.dashboardReports,
         *     access: ROUTE_PATHS.dashboard, element: page(() => import('...')) },
         * ],
         */
      },
      {
        /**
         * Job listings. The parent owns the nav entry and the crumb; the index
         * child renders the list, and the rest appear inside its <Outlet />.
         */
        id: 'job-listings',
        name: 'Job listings',
        path: paths.jobListings.list,
        icon: Briefcase,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.jobListings,
        children: [
          {
            id: 'job-listings-index',
            index: true,
            element: page(() => import('@/features/jobListings/pages/JobListingsPage')),
          },
          {
            id: 'job-listing-new',
            name: 'New listing',
            path: paths.jobListings.new,
            access: ROUTE_PATHS.jobListings,
            element: page(() => import('@/features/jobListings/pages/JobListingCreatePage')),
          },
          {
            id: 'job-listing-detail',
            name: 'Listing details',
            path: paths.jobListings.detail,
            dynamicCrumb: ({ params }) => ({
              queryKey: queryKeys.jobListings.detail(params.jobId),
              field: 'title',
              fallback: 'Listing details',
            }),
            element: page(() => import('@/features/jobListings/pages/JobListingDetailPage')),
          },
          {
            id: 'job-listing-edit',
            name: 'Edit listing',
            path: paths.jobListings.edit,
            access: ROUTE_PATHS.jobListings,
            element: page(() => import('@/features/jobListings/pages/JobListingEditPage')),
          },
        ],
      },
      {
        /**
         * Applicants who came in through job listings, filtered by department,
         * lead type, listing and date, with a CSV export of the selection.
         * Admins and managers only: the API refuses anyone else, since every row
         * carries contact details.
         */
        id: 'listing-leads',
        name: 'Listing leads',
        path: paths.listingLeads,
        icon: FileSpreadsheet,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.jobListings,
        roles: [ROLES.admin, ROLES.manager],
        element: page(() => import('@/features/jobListings/pages/ListingLeadsPage')),
      },
      {
        /**
         * Hiring pipeline. Which leads a person sees inside it is decided by the
         * API from their pipeline config — this grant only decides whether the
         * page exists for them at all.
         */
        id: 'hiring',
        name: 'Hiring',
        path: paths.hiring,
        icon: Users,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.hiring,
        element: page(() => import('@/features/hiring/pages/HiringPipelinePage')),
      },
      {
        /**
         * The interview desk — the calendar the bot books against, by week.
         *
         * Its own page rather than a tab on the pipeline: the pipeline answers
         * "what is on my list" and this answers "what does Thursday look like",
         * and the second question is about the day rather than about any lead.
         *
         * Scope comes from role on the server — a recruiter sees their own
         * calendar whatever they ask for — so this grant only decides whether the
         * page exists for them at all.
         */
        id: 'interviews',
        name: 'Interview desk',
        path: paths.interviews,
        icon: CalendarDays,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.interviews,
        element: page(() => import('@/features/interviews/pages/InterviewDeskPage')),
      },
      {
        /**
         * The hired roster.
         *
         * Its own page rather than a filter on the pipeline, because it answers a
         * question the queue is not shaped for: the queue is work to be done, and
         * a hire is work finished. Scope is by whose hire it was — a hire has left
         * every working stage, so the queue's ownership rule would show a
         * recruiter nothing.
         */
        id: 'hired',
        name: 'Hired',
        path: paths.hired,
        icon: UserCheck,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.hired,
        element: page(() => import('@/features/hired/pages/HiredPage')),
      },
      {
        /**
         * A recruiter's own figures.
         *
         * Gated by ROLE rather than by a grant: their own numbers are theirs by
         * virtue of the job, and putting it behind `access_control` would mean
         * handing the grant out by hand to every new hire — and quietly having
         * some of them without it. No `access` for the same reason.
         *
         * Recruiters only. An administrator or a manager reads the same figures
         * through the recruiter table on the Overview, where they can read
         * anybody's; this page has no answer for somebody who owns no leads.
         */
        id: 'my-performance',
        name: 'My performance',
        path: paths.myPerformance,
        icon: Gauge,
        showInNav: true,
        section: 'main',
        roles: [ROLES.recruiter],
        element: page(() => import('@/features/dashboard/pages/MyPerformancePage')),
      },
      {
        /**
         * WhatsApp inbox. The conversations for the leads this person owns — a
         * recruiter's own, every lead in the departments a manager pitches
         * (read-only unless it is theirs), everything for an administrator.
         * The scope comes from lead ownership on the server, so this grant only
         * decides whether the page exists for them at all.
         */
        id: 'inbox',
        name: 'Inbox',
        path: paths.inbox,
        icon: MessagesSquare,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.inbox,
        element: page(() => import('@/features/communications/pages/InboxPage')),
      },
      {
        /**
         * Meta ads. Campaign performance read straight from the Graph API, and the
         * leads Meta has sent us that are waiting to be brought into the pipeline.
         * Read-only against Meta — nothing here changes a campaign.
         */
        id: 'meta-ads',
        name: 'Meta ads',
        path: paths.meta,
        icon: Megaphone,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.meta,
        element: page(() => import('@/features/meta/pages/MetaAdsPage')),
      },
      {
        /**
         * Download staged Meta leads as CSV, picked by form/ad over a date+time
         * range. A separate route rather than a tab: it is an export workbench, not
         * another view of the queue, and gets its own shareable URL. Same read grant
         * as the Meta page — reporting, not an action — and listed under Meta ads in
         * the sidebar, as well as reachable from the Meta page header.
         */
        id: 'meta-leads-export',
        name: 'Export leads',
        path: paths.metaExport,
        icon: Download,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.meta,
        element: page(() => import('@/features/meta/pages/MetaLeadsExportPage')),
      },
      {
        /**
         * The bot's screening scripts and the WhatsApp templates that open a
         * conversation. Readable by anyone who imports leads — they have to
         * choose a script per batch — but only an administrator can edit one,
         * which the API enforces regardless of this grant.
         */
        id: 'screening',
        name: 'Screening scripts',
        path: paths.screening.list,
        icon: ClipboardList,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.screening,
        children: [
          {
            id: 'screening-index',
            index: true,
            element: page(() => import('@/features/screening/pages/ScreeningPage')),
          },
          {
            /**
             * A script is a page rather than a drawer: it is a long form with a
             * repeating section, and it is worth a URL somebody can send.
             */
            id: 'screening-new',
            name: 'New script',
            path: paths.screening.new,
            access: ROUTE_PATHS.screening,
            element: page(() => import('@/features/screening/pages/ScreeningConfigCreatePage')),
          },
          {
            id: 'screening-edit',
            name: 'Edit script',
            path: paths.screening.edit,
            access: ROUTE_PATHS.screening,
            element: page(() => import('@/features/screening/pages/ScreeningConfigEditPage')),
          },
        ],
      },
      {
        /**
         * The scheduling bot: what it is holding right now, and what its
         * screening has produced. Oversight rather than per-lead work, which is
         * why it is its own page rather than a tab on the scripts one.
         */
        id: 'bot',
        name: 'Scheduling bot',
        path: paths.bot,
        icon: Bot,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.bot,
        element: page(() => import('@/features/screening/pages/BotPage')),
      },
      {
        /**
         * The careers-site blog. Gated by role, not by a grant: writing posts is
         * what a content writer is for, and the API admits exactly these two
         * roles to the editor endpoints whatever a user's access list says.
         */
        id: 'blogs',
        name: 'Blog',
        path: paths.blogs.list,
        icon: Newspaper,
        showInNav: true,
        section: 'main',
        roles: [ROLES.admin, ROLES.contentWriter],
        children: [
          {
            id: 'blogs-index',
            index: true,
            element: page(() => import('@/features/blogs/pages/BlogPostsPage')),
          },
          {
            id: 'blog-new',
            name: 'New post',
            path: paths.blogs.new,
            element: page(() => import('@/features/blogs/pages/BlogPostCreatePage')),
          },
          {
            id: 'blog-edit',
            name: 'Edit post',
            path: paths.blogs.edit,
            dynamicCrumb: ({ params }) => ({
              queryKey: queryKeys.blogs.detail(params.postId),
              field: 'title',
              fallback: 'Edit post',
            }),
            element: page(() => import('@/features/blogs/pages/BlogPostEditPage')),
          },
        ],
      },
      {
        /**
         * User management. Accounts, pipeline config and the access grants
         * themselves — normally administrators only.
         */
        id: 'users',
        name: 'Users',
        path: paths.users,
        icon: UsersRound,
        showInNav: true,
        section: 'main',
        access: ROUTE_PATHS.users,
        element: page(() => import('@/features/users/pages/UsersPage')),
      },
      // {
      //   id: 'design-system',
      //   name: 'Design system',
      //   path: paths.designSystem,
      //   icon: Palette,
      //   showInNav: true,
      //   section: 'main',
      //   access: ROUTE_PATHS.users,
      //   element: page(() => import('@/features/designSystem/pages/DesignSystemPage')),
      // },
      {
        id: 'unauthorized',
        name: 'Access denied',
        path: paths.unauthorized,
        element: page(() => import('@/features/errors/pages/UnauthorizedPage')),
      },
      {
        id: 'not-found',
        name: 'Page not found',
        path: '*',
        hideCrumb: true,
        element: page(() => import('@/features/errors/pages/NotFoundPage')),
      },
    ],
  },
]
